// "Report a language issue": a quiet button under a lesson, story, dictionary entry or grammar page that opens a short
// form (what kind of mistake, what is wrong, and for guests an optional address for a reply). It sends only when the
// learner presses Send; reports land in /admin/reports for the team.

import type { FC, FormEvent, ReactNode } from 'react'
import type { ReportCourse, ReportKind } from '@/lib/reports'

import { useEffect, useId, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { CircleCheck, Flag, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/client/session'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { REPORT_KINDS, REPORT_LIMITS } from '@/lib/reports'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog'

interface ReportIssueButtonProps {
    course: ReportCourse
    /** Review key of what is on screen (lesson|u1-…, story|…, lex|noun|…). */
    itemKey?: string
    /** A readable name for it (the lesson or story title). */
    itemLabel?: string
    className?: string
}

type SendState =
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'sent' }
    | { kind: 'error', message: string }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const ReportIssueButton: FC<ReportIssueButtonProps> = ({ course, itemKey, itemLabel, className }): ReactNode => {
    const router = useRouter()
    const { user } = useSession()
    const baseId = useId()
    const [open, setOpen] = useState(false)
    const [kind, setKind] = useState<ReportKind>('spelling')
    const [message, setMessage] = useState('')
    const [email, setEmail] = useState('')
    const [website, setWebsite] = useState('')
    const [state, setState] = useState<SendState>({ kind: 'idle' })
    const [problem, setProblem] = useState<'message' | 'email' | null>(null)
    const messageRef = useRef<HTMLTextAreaElement>(null)
    const emailRef = useRef<HTMLInputElement>(null)
    const doneRef = useRef<HTMLButtonElement>(null)

    const clean = message.trim()
    const sending = state.kind === 'sending'
    const sent = state.kind === 'sent'
    const guest = !user
    const ids = {
        message: `${baseId}-message`,
        count: `${baseId}-count`,
        messageError: `${baseId}-message-error`,
        email: `${baseId}-email`,
        emailHelp: `${baseId}-email-help`,
        emailError: `${baseId}-email-error`,
        website: `${baseId}-website`
    }

    // After sending, the form is gone: focus moves to the Close button so the keyboard is never lost.
    useEffect(() => {
        if (sent) doneRef.current?.focus()
    }, [sent])

    const reset = () => {
        setKind('spelling')
        setMessage('')
        setEmail('')
        setWebsite('')
        setProblem(null)
        setState({ kind: 'idle' })
    }

    const changeOpen = (next: boolean) => {
        setOpen(next)

        // A sent report starts fresh next time; an unsent draft is kept if the dialog was closed by accident.
        if (!next && (state.kind === 'sent' || state.kind === 'error')) {
            if (state.kind === 'sent') reset()
            else setState({ kind: 'idle' })
        }
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (sending || sent) return

        if (clean.length < REPORT_LIMITS.messageMin) {
            setProblem('message')
            messageRef.current?.focus()

            return
        }

        if (guest && email.trim() && !EMAIL.test(email.trim())) {
            setProblem('email')
            emailRef.current?.focus()

            return
        }

        setProblem(null)
        setState({ kind: 'sending' })

        try {
            const page = (router.asPath.split(/[?#]/)[0] || '/').slice(0, REPORT_LIMITS.page)
            const res = await fetch('/api/reports', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    course,
                    page,
                    itemKey: itemKey ? itemKey.slice(0, REPORT_LIMITS.itemKey) : undefined,
                    itemLabel: itemLabel ? itemLabel.slice(0, REPORT_LIMITS.itemLabel) : undefined,
                    kind,
                    message: clean,
                    email: guest && email.trim() ? email.trim() : undefined,
                    website
                })
            })
            const data = await res.json().catch(() => ({})) as { ok?: boolean, error?: string }

            if (!res.ok || !data.ok) {
                setState({
                    kind: 'error',
                    message: res.status === 429 && data.error
                        ? data.error
                        : 'रिपोर्ट नहीं भेजी जा सकी। थोड़ी देर बाद फिर प्रयास करें। · The report could not be sent. Please try again in a moment.'
                })

                return
            }

            setState({ kind: 'sent' })
        }
        catch {
            setState({ kind: 'error', message: 'इंटरनेट से जुड़ नहीं सके। कनेक्शन जाँचकर फिर भेजें। · We could not connect. Check your connection and send again.' })
        }
    }

    return (
        <Dialog open={open} onOpenChange={next => changeOpen(next)}>
            <DialogTrigger
                className={cn(
                    'inline-flex min-h-tap items-center gap-2 rounded-full px-3 text-left text-sm font-medium text-muted-foreground transition-colors',
                    'hover:bg-secondary hover:text-foreground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    'in-data-[mode=kids]:hover:bg-kid-sky/15',
                    className
                )}
            >
                <Flag className='size-4 shrink-0' aria-hidden='true' />
                <span>
                    <span lang='hi' className='hindi'>भाषा की त्रुटि बताएँ</span>
                    <span aria-hidden='true' className='text-muted-foreground/70'> · </span>
                    <span lang='en'>Report a language issue</span>
                </span>
            </DialogTrigger>

            <DialogContent className='max-h-[90dvh] overflow-y-auto sm:max-w-lg'>
                <form onSubmit={submit} noValidate className='grid gap-4'>
                    <DialogHeader className='pr-8'>
                        <DialogTitle className='text-lg leading-snug'>
                            <span lang='hi' className='hindi block'>भाषा की त्रुटि बताएँ</span>
                            <span lang='en' className='mt-1 block text-sm font-normal text-muted-foreground'>Report a language issue</span>
                        </DialogTitle>
                        <DialogDescription>
                            <span lang='hi' className='hindi'>कोई वर्तनी, रूप, अर्थ या उच्चारण ग़लत लगे तो बताइए। हमारे शिक्षक हर रिपोर्ट देखते हैं।</span>
                            <span aria-hidden='true'> · </span>
                            <span lang='en'>Spotted a wrong spelling, form, meaning or sound? Tell us. Our teachers read every report.</span>
                        </DialogDescription>
                        {itemLabel && (
                            <p className='rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground'>
                                <span lang='hi' className='hindi'>किस बारे में:</span> <span lang='en'>About:</span>{' '}
                                <span className='font-medium text-foreground'>{itemLabel}</span>
                            </p>
                        )}
                    </DialogHeader>

                    {!sent && (
                        <>
                            <fieldset className='grid gap-2' disabled={sending}>
                                <legend className='mb-2 text-sm font-medium'>
                                    <span lang='hi' className='hindi'>किस तरह की त्रुटि?</span>
                                    <span aria-hidden='true' className='text-muted-foreground/70'> · </span>
                                    <span lang='en'>What kind of mistake?</span>
                                </legend>
                                <div className='grid gap-2 sm:grid-cols-2'>
                                    {REPORT_KINDS.map(option => (
                                        <label
                                            key={option.id}
                                            className={cn(
                                                'flex min-h-tap cursor-pointer items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2 text-sm transition-colors',
                                                'hover:border-saffron/60 hover:bg-saffron/5',
                                                'has-[:checked]:border-primary has-[:checked]:bg-primary/10 has-[:checked]:font-semibold',
                                                'has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring'
                                            )}
                                        >
                                            <input
                                                type='radio'
                                                name={`${baseId}-kind`}
                                                value={option.id}
                                                checked={kind === option.id}
                                                onChange={() => setKind(option.id)}
                                                className='peer sr-only'
                                            />
                                            <span
                                                aria-hidden='true'
                                                className='flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground/60 peer-checked:border-primary peer-checked:bg-primary peer-checked:shadow-[inset_0_0_0_2px_var(--background)]'
                                            />
                                            <span className='flex flex-col leading-tight'>
                                                <span lang='hi' className='hindi'>{option.hi}</span>
                                                <span lang='en' className='text-xs font-normal text-muted-foreground'>{option.en}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            <div className='grid gap-1.5'>
                                <label htmlFor={ids.message} className='text-sm font-medium'>
                                    <span lang='hi' className='hindi'>क्या ग़लत है?</span>
                                    <span aria-hidden='true' className='text-muted-foreground/70'> · </span>
                                    <span lang='en'>What is wrong?</span>
                                    <span className='font-normal text-muted-foreground'> (<span lang='hi' className='hindi'>ज़रूरी</span> · <span lang='en'>required</span>)</span>
                                </label>
                                <Textarea
                                    ref={messageRef}
                                    id={ids.message}
                                    value={message}
                                    onChange={event => {
                                        setMessage(event.target.value.slice(0, REPORT_LIMITS.messageMax))
                                        if (problem === 'message') setProblem(null)
                                    }}
                                    rows={4}
                                    required
                                    minLength={REPORT_LIMITS.messageMin}
                                    maxLength={REPORT_LIMITS.messageMax}
                                    disabled={sending}
                                    aria-invalid={problem === 'message' ? true : undefined}
                                    aria-describedby={problem === 'message' ? `${ids.count} ${ids.messageError}` : ids.count}
                                    placeholder='जैसे: “पढइ” का अर्थ “पढ़ता है” होना चाहिए · e.g. the meaning should be “reads”'
                                    className='min-h-24 text-base'
                                />
                                <div className='flex flex-wrap items-start justify-between gap-2'>
                                    {problem === 'message' ? (
                                        <p id={ids.messageError} className='text-sm font-medium text-danger'>
                                            <span lang='hi' className='hindi'>कृपया कम से कम {REPORT_LIMITS.messageMin} अक्षर लिखें।</span>
                                            <span aria-hidden='true'> · </span>
                                            <span lang='en'>Please write at least {REPORT_LIMITS.messageMin} characters.</span>
                                        </p>
                                    ) : <span />}
                                    <p id={ids.count} className='text-xs tabular-nums text-muted-foreground'>
                                        {clean.length} / {REPORT_LIMITS.messageMax}
                                    </p>
                                </div>
                            </div>

                            {guest && (
                                <div className='grid gap-1.5'>
                                    <label htmlFor={ids.email} className='text-sm font-medium'>
                                        <span lang='hi' className='hindi'>आपका ईमेल</span>
                                        <span aria-hidden='true' className='text-muted-foreground/70'> · </span>
                                        <span lang='en'>Your email</span>
                                        <span className='font-normal text-muted-foreground'> (<span lang='hi' className='hindi'>वैकल्पिक</span> · <span lang='en'>optional</span>)</span>
                                    </label>
                                    <Input
                                        ref={emailRef}
                                        id={ids.email}
                                        type='email'
                                        inputMode='email'
                                        autoComplete='email'
                                        value={email}
                                        onChange={event => {
                                            setEmail(event.target.value.slice(0, REPORT_LIMITS.email))
                                            if (problem === 'email') setProblem(null)
                                        }}
                                        maxLength={REPORT_LIMITS.email}
                                        disabled={sending}
                                        aria-invalid={problem === 'email' ? true : undefined}
                                        aria-describedby={problem === 'email' ? `${ids.emailHelp} ${ids.emailError}` : ids.emailHelp}
                                        className='h-11 text-base'
                                    />
                                    <p id={ids.emailHelp} className='text-xs text-muted-foreground'>
                                        <span lang='hi' className='hindi'>केवल तभी, जब आप उत्तर चाहते हैं।</span>
                                        <span aria-hidden='true'> · </span>
                                        <span lang='en'>Only if you would like a reply.</span>
                                    </p>
                                    {problem === 'email' && (
                                        <p id={ids.emailError} className='text-sm font-medium text-danger'>
                                            <span lang='hi' className='hindi'>यह ईमेल पता ठीक नहीं लगता।</span>
                                            <span aria-hidden='true'> · </span>
                                            <span lang='en'>This email address does not look right.</span>
                                        </p>
                                    )}
                                </div>
                            )}

                            <div aria-hidden='true' className='absolute -left-[10000px] h-px w-px overflow-hidden'>
                                <label htmlFor={ids.website}>Leave this field empty</label>
                                <input
                                    id={ids.website}
                                    name='website'
                                    type='text'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={website}
                                    onChange={event => setWebsite(event.target.value)}
                                />
                            </div>
                        </>
                    )}

                    {/* Always in the page (out of the layout while empty), so the result is announced when it appears. */}
                    <div role='status' aria-live='polite' className={cn(!sent && state.kind !== 'error' && 'sr-only')}>
                        {sent && (
                            <p className='flex items-start gap-2 rounded-xl border border-success/40 bg-success-soft px-4 py-3 text-sm'>
                                <CircleCheck className='mt-0.5 size-5 shrink-0 text-success' aria-hidden='true' />
                                <span>
                                    <span lang='hi' className='hindi block font-semibold'>धन्यवाद! हमारे शिक्षक इसे देखेंगे।</span>
                                    <span lang='en' className='block text-muted-foreground'>Thank you. A teacher will check it.</span>
                                </span>
                            </p>
                        )}
                        {state.kind === 'error' && (
                            <p className='rounded-xl border border-danger/40 bg-danger-soft px-4 py-3 text-sm font-medium text-danger'>{state.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        {sent ? (
                            <Button ref={doneRef} type='button' className='h-11 px-5' onClick={() => changeOpen(false)}>
                                <span lang='hi' className='hindi'>बन्द करें</span>
                                <span aria-hidden='true' className='opacity-70'>·</span>
                                <span lang='en'>Close</span>
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type='button'
                                    variant='outline'
                                    className='h-11 px-4'
                                    disabled={sending}
                                    onClick={() => {
                                        reset()
                                        setOpen(false)
                                    }}
                                >
                                    <span lang='hi' className='hindi'>रद्द करें</span>
                                    <span aria-hidden='true' className='opacity-70'>·</span>
                                    <span lang='en'>Cancel</span>
                                </Button>
                                <Button type='submit' className='h-11 px-5' disabled={sending}>
                                    {sending
                                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                        : <Send className='size-4' aria-hidden='true' />}
                                    <span lang='hi' className='hindi'>{sending ? 'भेज रहे हैं…' : 'भेजें'}</span>
                                    <span aria-hidden='true' className='opacity-70'>·</span>
                                    <span lang='en'>{sending ? 'Sending…' : 'Send'}</span>
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default ReportIssueButton
